/**
 * QUICK CAPTURE — admin walkthrough that captures real-song audio for every
 * show in a sitting. One artist on screen at a time: hit Capture → pick tab
 * → 10 s recording → Hedra regen (~90 s) → auto-advance to next.
 *
 * Flow per artist:
 *   1. "Capture from Tab Audio" button → getDisplayMedia({audio:true})
 *   2. MediaRecorder for 10 s
 *   3. POST → /admin/shows/{id}/lipsync-from-upload → job_id
 *   4. Poll /admin/video-jobs/{job_id} every 4 s until complete or error
 *   5. Auto-advance after 1.5 s green pulse
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Mic, SkipForward, Check, X as XIcon, Square } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";

const RECORD_SECONDS = 10;

export default function QuickCapture() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("idle"); // idle | sharing | recording | uploading | generating | complete | error
  const [elapsed, setElapsed] = useState(0);
  const [jobElapsed, setJobElapsed] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [completed, setCompleted] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("qc_completed") || "[]")); } catch { return new Set(); }
  });

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const jobTimerRef = useRef(null);

  const supportsTabCapture = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia;

  // Gate: must be logged-in admin
  useEffect(() => {
    if (user && user.role !== "admin") navigate("/hologram-club");
  }, [user, navigate]);

  // Load all shows once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/admin/shows");
        setShows(data || []);
      } catch (e) { toast.error("Failed to load shows"); }
    })();
  }, []);

  // Persist completed set
  useEffect(() => {
    try { localStorage.setItem("qc_completed", JSON.stringify(Array.from(completed))); } catch {}
  }, [completed]);

  // Cleanup on unmount
  useEffect(() => () => cleanupAll(), []);

  const cleanupAll = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    streamRef.current = null;
    try { clearInterval(timerRef.current); } catch {}
    try { clearInterval(jobTimerRef.current); } catch {}
    timerRef.current = null;
    jobTimerRef.current = null;
  };

  const show = shows[idx];

  const goNext = () => {
    cleanupAll();
    setPhase("idle");
    setElapsed(0);
    setJobElapsed(0);
    setErrorMsg("");
    setIdx((i) => Math.min(shows.length - 1, i + 1));
  };

  const goPrev = () => {
    cleanupAll();
    setPhase("idle");
    setElapsed(0);
    setJobElapsed(0);
    setErrorMsg("");
    setIdx((i) => Math.max(0, i - 1));
  };

  const startCapture = async () => {
    if (!supportsTabCapture || !show) return;
    setPhase("sharing");
    setErrorMsg("");
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        stream.getTracks().forEach((t) => t.stop());
        setPhase("idle");
        toast.error("No tab audio — re-share and tick 'Share tab audio'.");
        return;
      }
      stream.getVideoTracks().forEach((t) => t.stop());
      const audioOnly = new MediaStream(audioTracks);
      streamRef.current = audioOnly;

      const rec = new MediaRecorder(audioOnly, { mimeType: "audio/webm;codecs=opus" });
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 5_000) {
          toast.error("Recording too short / silent.");
          setPhase("idle");
          return;
        }
        await uploadAndQueue(blob);
      };
      rec.start(250);
      setPhase("recording");
      setElapsed(0);
      const startedAt = Date.now();
      timerRef.current = setInterval(() => {
        const sec = (Date.now() - startedAt) / 1000;
        setElapsed(sec);
        if (sec >= RECORD_SECONDS) {
          try { rec.stop(); } catch {}
          clearInterval(timerRef.current);
        }
      }, 100);
    } catch (e) {
      cleanupAll();
      setPhase("idle");
      if (e?.name !== "NotAllowedError") {
        setErrorMsg(`Tab capture failed: ${e?.message || e}`);
      }
    }
  };

  const stopEarly = () => {
    try { recorderRef.current?.stop(); } catch {}
    try { clearInterval(timerRef.current); } catch {}
  };

  const uploadAndQueue = async (blob) => {
    setPhase("uploading");
    const fd = new FormData();
    fd.append("audio", new File([blob], `${show.artist}-capture.webm`, { type: "audio/webm" }));
    fd.append("duration_seconds", String(Math.max(4, Math.min(30, Math.round(elapsed || RECORD_SECONDS)))));
    try {
      const { data } = await http.post(`/admin/shows/${show.id}/lipsync-from-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhase("generating");
      setJobElapsed(0);
      const startedAt = Date.now();
      jobTimerRef.current = setInterval(async () => {
        setJobElapsed((Date.now() - startedAt) / 1000);
        try {
          const { data: job } = await http.get(`/admin/video-jobs/${data.job_id}`);
          if (job.status === "complete") {
            clearInterval(jobTimerRef.current);
            setCompleted((c) => new Set([...c, show.id]));
            setPhase("complete");
            // Auto-advance after a brief green pulse
            setTimeout(() => {
              if (idx < shows.length - 1) goNext();
            }, 1800);
          } else if (job.status === "error") {
            clearInterval(jobTimerRef.current);
            setErrorMsg(job.error || "Hedra job failed");
            setPhase("error");
          }
        } catch (e) { /* keep polling */ }
      }, 4000);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Upload failed");
      setPhase("error");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="qc-login-gate">
        <div className="text-center">
          <p className="font-mono-ui text-[11px] tracking-[0.32em] uppercase text-white/60 mb-4">Admin only</p>
          <button onClick={() => navigate("/hologram-club/login")} className="btn-neon">Log In</button>
        </div>
      </div>
    );
  }
  if (shows.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="qc-loading">
        <p className="font-mono-ui text-[11px] tracking-[0.32em] uppercase text-white/60">Loading shows…</p>
      </div>
    );
  }

  const doneCount = completed.size;
  const totalCount = shows.length;
  const charSrc = show?.character_image_url
    ? (show.character_image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${show.character_image_url}` : show.character_image_url)
    : show?.image;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="quick-capture-page">
      {/* Top bar */}
      <header className="border-b border-white/10 px-6 md:px-12 py-5 flex items-center justify-between flex-wrap gap-4">
        <button onClick={() => navigate("/hologram-club/admin")} className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/60 hover:text-[#00E5FF] inline-flex items-center gap-2" data-testid="qc-back">
          <ArrowLeft size={14} /> Back to Admin
        </button>
        <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#00E5FF] inline-flex items-center gap-2">
          <span className="ring-dot" /> QUICK CAPTURE · LIP-SYNC ALL
        </div>
        <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/70" data-testid="qc-progress">
          {String(doneCount).padStart(2, "0")} / {String(totalCount).padStart(2, "0")} DONE
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16">
        {/* Progress dots */}
        <div className="grid gap-1.5 mb-12" style={{ gridTemplateColumns: `repeat(${shows.length}, minmax(0, 1fr))` }}>
          {shows.map((s, i) => (
            <div
              key={s.id}
              className={`h-1.5 transition-colors ${
                completed.has(s.id)
                  ? "bg-gradient-to-r from-[#00E5FF] to-[#FF007F]"
                  : i === idx
                  ? "bg-[#FFD166]"
                  : "bg-white/10"
              }`}
              data-testid={`qc-dot-${i}`}
            />
          ))}
        </div>

        {/* Current artist hero */}
        <div className="grid md:grid-cols-[300px_1fr] gap-8 md:gap-12 items-center">
          <div className="relative aspect-[3/4] border border-white/10 overflow-hidden bg-[#0B0608]">
            {charSrc && (
              <img src={charSrc} alt={show.artist} className="absolute inset-0 w-full h-full object-cover hologram-isolated" />
            )}
            <div className="absolute inset-0 hologram-cyan-veil pointer-events-none" />
            <div className="absolute inset-0 hologram-scanlines-overlay pointer-events-none" />
            {completed.has(show.id) && (
              <div className="absolute top-3 right-3 inline-flex items-center gap-1 border border-[#00E5FF] bg-[#00E5FF] text-black px-2 py-1 font-mono-ui text-[10px] tracking-[0.28em] uppercase font-bold">
                <Check size={11} /> Synced
              </div>
            )}
          </div>

          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#00E5FF]">
              ARTIST {idx + 1} OF {totalCount} · {(show.venue || "vegas").toUpperCase()}
            </div>
            <h2 className="mt-3 font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.88]" data-testid="qc-artist-name">
              {show.artist}
            </h2>
            {show.tagline && (
              <p className="mt-4 text-white/65 text-sm md:text-base max-w-xl">{show.tagline}</p>
            )}
            {show.youtube_url && (
              <a
                href={show.youtube_url}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FF0000] hover:text-white"
                data-testid="qc-open-yt"
              >
                ▶ Open this artist&rsquo;s YouTube in a new tab
              </a>
            )}

            {/* Action area changes per phase */}
            <div className="mt-8">
              <AnimatePresence mode="wait">
                {(phase === "idle" || phase === "sharing") && (
                  <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="qc-phase-idle">
                    <p className="text-white/65 text-sm leading-relaxed max-w-lg">
                      <span className="text-[#00E5FF]">1.</span> Open the song in another tab and start playing it.{" "}
                      <span className="text-[#00E5FF]">2.</span> Click Capture below.{" "}
                      <span className="text-[#00E5FF]">3.</span> Pick that tab and tick <span className="text-[#FFD166]">&ldquo;Share tab audio&rdquo;</span>.
                    </p>
                    {!supportsTabCapture && (
                      <div className="mt-4 border border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F] p-3 font-mono-ui text-[10px] tracking-[0.18em] uppercase">
                        This browser can&rsquo;t capture tab audio. Use Chrome or Edge.
                      </div>
                    )}
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <button
                        onClick={startCapture}
                        disabled={!supportsTabCapture || phase === "sharing"}
                        className="btn-neon disabled:opacity-60 inline-flex items-center gap-2"
                        data-testid="qc-capture-btn"
                      >
                        <Mic size={14} /> {phase === "sharing" ? "Pick a tab…" : `Capture ${RECORD_SECONDS}s from Tab`}
                      </button>
                      <button onClick={goNext} className="btn-ghost text-[10px] py-3 px-4 inline-flex items-center gap-2" data-testid="qc-skip-btn">
                        <SkipForward size={12} /> Skip this artist
                      </button>
                    </div>
                  </motion.div>
                )}

                {phase === "recording" && (
                  <motion.div key="rec" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="qc-phase-recording">
                    <div className="border border-[#FF007F] bg-[#FF007F]/10 p-5">
                      <div className="font-mono-ui text-[11px] tracking-[0.32em] uppercase text-[#FF007F] inline-flex items-center gap-2">
                        <span className="ring-dot" /> RECORDING · {elapsed.toFixed(1)}s / {RECORD_SECONDS}s
                      </div>
                      <div className="mt-3 h-2 bg-black/60 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#00E5FF] to-[#FF007F] transition-all" style={{ width: `${Math.min(100, (elapsed / RECORD_SECONDS) * 100)}%` }} />
                      </div>
                      <button onClick={stopEarly} className="btn-ghost text-[10px] py-2 px-3 mt-4 inline-flex items-center gap-2" data-testid="qc-stop-early">
                        <Square size={11} /> Stop now & upload
                      </button>
                    </div>
                  </motion.div>
                )}

                {(phase === "uploading" || phase === "generating") && (
                  <motion.div key="proc" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="qc-phase-generating">
                    <div className="border border-[#00E5FF]/40 bg-[#00E5FF]/5 p-5">
                      <div className="font-mono-ui text-[11px] tracking-[0.32em] uppercase text-[#00E5FF] inline-flex items-center gap-2">
                        <span className="ring-dot" />
                        {phase === "uploading" ? "UPLOADING…" : `HEDRA LIP-SYNC · ${jobElapsed.toFixed(0)}s / ~90s`}
                      </div>
                      {phase === "generating" && (
                        <div className="mt-3 h-2 bg-black/60 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-[#00E5FF] to-[#FFD166] transition-all" style={{ width: `${Math.min(100, (jobElapsed / 90) * 100)}%` }} />
                        </div>
                      )}
                      <p className="mt-3 text-white/55 text-xs">Auto-advances to next artist when complete. You can stay on this page or open YouTube to queue the next song.</p>
                    </div>
                  </motion.div>
                )}

                {phase === "complete" && (
                  <motion.div key="ok" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} data-testid="qc-phase-complete">
                    <div className="border border-[#00E5FF] bg-[#00E5FF]/10 p-5">
                      <div className="font-mono-ui text-[11px] tracking-[0.32em] uppercase text-[#00E5FF] inline-flex items-center gap-2">
                        <Check size={14} /> SYNCED · advancing to next…
                      </div>
                    </div>
                  </motion.div>
                )}

                {phase === "error" && (
                  <motion.div key="err" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="qc-phase-error">
                    <div className="border border-[#FF007F] bg-[#FF007F]/10 p-5">
                      <div className="font-mono-ui text-[11px] tracking-[0.32em] uppercase text-[#FF007F] inline-flex items-center gap-2">
                        <XIcon size={14} /> ERROR
                      </div>
                      <p className="mt-2 text-white/70 text-sm">{errorMsg}</p>
                      <div className="mt-4 flex flex-wrap gap-3">
                        <button onClick={() => { setPhase("idle"); setErrorMsg(""); }} className="btn-ghost text-[10px] py-2 px-3" data-testid="qc-retry">Try again</button>
                        <button onClick={goNext} className="btn-ghost text-[10px] py-2 px-3 inline-flex items-center gap-2" data-testid="qc-skip-after-err">
                          <SkipForward size={11} /> Skip
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Nav controls */}
            <div className="mt-10 flex items-center justify-between flex-wrap gap-3 border-t border-white/5 pt-6">
              <button
                onClick={goPrev}
                disabled={idx === 0 || phase === "recording" || phase === "uploading" || phase === "generating"}
                className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/55 hover:text-[#00E5FF] disabled:opacity-30 inline-flex items-center gap-2"
                data-testid="qc-prev-btn"
              >
                <ArrowLeft size={12} /> Previous
              </button>
              <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-white/40">
                {idx + 1} of {totalCount}
              </div>
              <button
                onClick={goNext}
                disabled={idx >= shows.length - 1 || phase === "recording" || phase === "uploading" || phase === "generating"}
                className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/55 hover:text-[#00E5FF] disabled:opacity-30 inline-flex items-center gap-2"
                data-testid="qc-next-btn"
              >
                Next <SkipForward size={12} />
              </button>
            </div>
          </div>
        </div>

        {idx >= shows.length - 1 && doneCount === totalCount && (
          <div className="mt-12 border border-[#00E5FF] bg-[#00E5FF]/10 p-6 text-center" data-testid="qc-all-done">
            <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#00E5FF]">ALL SYNCED</div>
            <h3 className="mt-3 font-display font-black tracking-tighter text-4xl">Every hologram now sings the real song.</h3>
            <button onClick={() => navigate("/hologram-club")} className="btn-neon mt-6" data-testid="qc-go-watch">View the floor</button>
          </div>
        )}
      </div>
    </div>
  );
}
